import React from 'react';
import { Row, Col, Alert } from 'react-bootstrap';
import { FaArrowUp, FaUsers, FaUserPlus } from 'react-icons/fa';

const PopulationMethodStep = ({ workflowData, setWorkflowData }) => {
  const transitionTypes = [
    {
      value: "simple",
      title: "Simple Grade Progression", 
      description: "Move entire classes up one grade level (1:1 mapping)",
      icon: FaArrowUp,
      useCase: "Single class per grade, or identical class structures",
      color: "#10b981", // Green
      examples: ["JI Red → SI Red", "1st Class → 2nd Class", "All students move together"]
    },
    {
      value: "complex", 
      title: "Custom Student Placement",
      description: "Flexible student selection with individual or bulk placement options", 
      icon: FaUsers,
      useCase: "Any scenario requiring student selection - individual, bulk, or mixed approaches",
      color: "#8b5cf6", // Purple
      examples: ["Individual student selection", "Bulk class selection", "Mix students across classes", "Redistribution needed"]
    },
    {
      value: "new",
      title: "New Students Only",
      description: "Add fresh students to the created classes",
      icon: FaUserPlus,
      useCase: "First-time enrollment or adding students to existing structure",
      color: "#3b82f6", // Blue
      examples: ["New enrollment", "Transfer students", "Fresh intake"]
    }
  ];

  const handleMethodSelect = (method) => {
    setWorkflowData(prev => ({
      ...prev,
      populationMethod: method
    }));
  };

  const handleCardMouseEnter = (e, type) => {
    if (workflowData.populationMethod !== type.value) {
      e.currentTarget.style.borderColor = type.color;
      e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
      e.currentTarget.style.boxShadow = `0 8px 25px ${type.color}30`;
    }
  };

  const handleCardMouseLeave = (e, type) => {
    if (workflowData.populationMethod !== type.value) {
      e.currentTarget.style.borderColor = '#e5e7eb';
      e.currentTarget.style.transform = 'translateY(0) scale(1)';
      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
    }
  };

  return (
    <div>
      <div className="mb-4">
        <h5>Choose Transition Type</h5>
        <p className="text-muted">
          Select the approach that best matches your school's situation for moving students to the new classes.
        </p>
      </div>
      
      <Row className="g-4">
        {transitionTypes.map((type, index) => (
          <Col key={type.value} md={index === 2 ? 12 : 6}>
            <div 
              className={`card h-100 ${
                workflowData.populationMethod === type.value 
                  ? 'border-primary' 
                  : 'border-light'
              }`}
              onClick={() => handleMethodSelect(type.value)}
              onMouseEnter={(e) => handleCardMouseEnter(e, type)}
              onMouseLeave={(e) => handleCardMouseLeave(e, type)}
              style={{ 
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                borderWidth: '2px',
                borderRadius: '12px',
                background: workflowData.populationMethod === type.value 
                  ? `linear-gradient(135deg, ${type.color}15, ${type.color}25)` 
                  : `linear-gradient(135deg, ${type.color}03, ${type.color}08)`,
                boxShadow: workflowData.populationMethod === type.value 
                  ? `0 8px 32px ${type.color}25, 0 0 0 1px ${type.color}20` 
                  : '0 2px 4px rgba(0,0,0,0.1)',
                transform: workflowData.populationMethod === type.value ? 'translateY(-2px)' : 'translateY(0)',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {/* Subtle gradient overlay for selected card */}
              {workflowData.populationMethod === type.value && (
                <div 
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: `linear-gradient(90deg, ${type.color}, ${type.color}80, ${type.color})`,
                    zIndex: 1
                  }}
                />
              )}
              
              <div className="card-body p-3" style={{ position: 'relative', zIndex: 2 }}>
                {/* Header with Icon and Title */}
                <div className="d-flex align-items-center mb-2">
                  <div 
                    className="rounded-circle d-flex align-items-center justify-content-center me-2"
                    style={{
                      width: '40px',
                      height: '40px',
                      background: `linear-gradient(135deg, ${type.color}20, ${type.color}35)`,
                      color: type.color,
                      border: `2px solid ${type.color}30`,
                      boxShadow: `0 2px 8px ${type.color}20`
                    }}
                  >
                    <type.icon size={18} />
                  </div>
                  <div className="flex-grow-1">
                    <h6 className="card-title mb-0 fw-bold" style={{ 
                      color: type.color,
                      fontSize: '1rem'
                    }}>
                      {type.title}
                    </h6>
                    {workflowData.populationMethod === type.value && (
                      <span 
                        className="badge rounded-pill px-2 py-1 mt-1"
                        style={{
                          backgroundColor: `${type.color}20`,
                          color: type.color,
                          border: `1px solid ${type.color}40`,
                          fontSize: '0.7rem',
                          fontWeight: '600'
                        }}
                      >
                        ✓ Selected
                      </span>
                    )}
                  </div>
                </div>

                {/* Description */}
                <p className="card-text mb-2 small" style={{
                  color: '#374151',
                  lineHeight: '1.4'
                }}>
                  {type.description}
                </p>

                {/* Use Case */}
                <div className="mb-2 p-2" style={{
                  backgroundColor: `${type.color}08`,
                  borderRadius: '6px',
                  borderLeft: `3px solid ${type.color}`
                }}>
                  <small className="fw-bold" style={{ color: type.color }}>
                    Best for: 
                  </small>
                  <span className="small ms-1" style={{ color: '#4b5563' }}>
                    {type.useCase}
                  </span>
                </div>

                {/* Workflow Preview */}
                <div className="mt-2 pt-2" style={{
                  borderTop: `1px solid ${type.color}20`
                }}>
                  <small className="fw-bold text-muted me-1">Next:</small>
                  <small style={{ color: type.color, fontWeight: '500' }}>
                    {type.value === 'simple' && "Select classes → Auto-assign"}
                    {type.value === 'complex' && "Select students → Place flexibly"}
                    {type.value === 'new' && "Add students → Assign to classes"}
                  </small>
                </div>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {/* Additional Information */}
      {workflowData.populationMethod && (
        <div className="mt-3">
          <Alert 
            variant="info" 
            className="d-flex align-items-start py-2"
            style={{
              border: `1px solid ${transitionTypes.find(t => t.value === workflowData.populationMethod)?.color}40`,
              backgroundColor: `${transitionTypes.find(t => t.value === workflowData.populationMethod)?.color}08`,
              borderRadius: '8px'
            }}
          >
            <div className="me-2">💡</div>
            <div>
              <strong className="small" style={{ 
                color: transitionTypes.find(t => t.value === workflowData.populationMethod)?.color 
              }}>
                {transitionTypes.find(t => t.value === workflowData.populationMethod)?.title}
              </strong>
              <div className="small mt-1">
                {workflowData.populationMethod === 'simple' && 
                  "Select source classes and students will be automatically moved to corresponding target classes."
                }
                {workflowData.populationMethod === 'complex' && 
                  "Full flexibility to select students individually, select entire classes, or use bulk operations."
                }
                {workflowData.populationMethod === 'new' && 
                  "Import student lists or manually add new students to populate your created classes."
                }
              </div>
            </div>
          </Alert>
        </div>
      )}

      {/* Help Section */}
      <div className="mt-3">
        <details className="mb-2">
          <summary className="text-muted small fw-bold" style={{ cursor: 'pointer' }}>
            🤔 Not sure which option to choose?
          </summary>
          <div className="mt-2 p-3 rounded" style={{
            background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)',
            border: '1px solid #dee2e6'
          }}>
            <Row className="g-2">
              <Col md={4}>
                <h6 className="small mb-1" style={{ color: '#10b981' }}>Choose Simple if:</h6>
                <ul className="small mb-0" style={{ fontSize: '0.8rem' }}>
                  <li>Clear 1:1 class mapping</li>
                  <li>Students move as groups</li>
                  <li>No redistribution needed</li>
                </ul>
              </Col>
              <Col md={4}>
                <h6 className="small mb-1" style={{ color: '#8b5cf6' }}>Choose Custom if:</h6>
                <ul className="small mb-0" style={{ fontSize: '0.8rem' }}>
                  <li>Complex placement scenarios</li>
                  <li>Individual/bulk selection</li>
                  <li>Need to redistribute students</li>
                </ul>
              </Col>
              <Col md={4}>
                <h6 className="small mb-1" style={{ color: '#3b82f6' }}>Choose New Students if:</h6>
                <ul className="small mb-0" style={{ fontSize: '0.8rem' }}>
                  <li>Fresh student enrollment</li>
                  <li>Transfer students</li>
                  <li>New intake period</li>
                </ul>
              </Col>
            </Row>
          </div>
        </details>
      </div>
    </div>
  );
};

export default PopulationMethodStep;